import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useCurrentUser } from "@/api/queries/me";
import { useDepartments } from "@/api/queries/departments";
import {
  useSopComplianceSummary,
  useSopExceedances,
  useSopRecommendations,
  useSopRosterExposure,
  useSopRuleResults,
  type SopComplianceSummary,
  type SopExceedances,
  type SopRecommendation,
  type SopRosterMember,
} from "@/api/queries/sop-compliance";
import { useAppStore } from "@/store/app-store";
import { useAuth } from "@/auth/AuthProvider";
import { isAdminRole, isRespaiAdmin } from "@/lib/permissions";
import { incidentDisplayName } from "@/api/models";
import { toNum } from "@/lib/format";
import { trackEvent, AnalyticsEvent, useDwellTracking } from "@/analytics";
import { TrackedSection } from "./TrackedSection";
import { SopFeedbackBlock } from "./SopFeedbackBlock";
import { DepartmentSopRules } from "./DepartmentSopRules";

/**
 * SOP Compliance & Exposure — a READ-ONLY PREVIEW for the chief view.
 *
 * Every figure comes from the typed server-side evaluation
 * (`/v1/sop-compliance/*`, RES-59/46) — the client renders, it never scores.
 * The illustrative recommendations stay flagged illustrative until the
 * department's own approved SOP rules (RES-58) take over; those render in
 * "Your department's SOPs". Visuals are hand-rolled CSS so they also print
 * cleanly. Nothing here certifies safety.
 */

const fmtInt = (n: number) => Math.round(n).toLocaleString();
const fmtPpm = (n: number | null, dp = 0) => (n === null ? "—" : n.toFixed(dp));
const plural = (n: number) => (n === 1 ? "" : "s");

/** Native print-to-PDF (vector, selectable text; no PDF deps). Print CSS hides the
 *  app chrome so only the report prints. document.title seeds the saved filename. */
function downloadPdf(deptName: string) {
  trackEvent(AnalyticsEvent.SopPdfDownloaded);
  const previous = document.title;
  document.title = `RespAI SOP Compliance — ${deptName}`;
  const restore = () => {
    document.title = previous;
    window.removeEventListener("afterprint", restore);
  };
  window.addEventListener("afterprint", restore);
  window.print();
}

export function SopCompliancePage() {
  const { identity } = useAuth();
  if (!isAdminRole(identity?.role)) {
    return (
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-semibold">SOP Compliance &amp; Exposure</h1>
        </header>
        <p className="text-sm text-destructive">
          SOP Compliance is restricted to chiefs and RespAI administrators.
        </p>
      </div>
    );
  }
  return <SopCompliancePageInner />;
}

function SopCompliancePageInner() {
  const { identity } = useAuth();
  const me = useCurrentUser();
  const isPlatformAdmin = isRespaiAdmin(identity?.role);
  const selectedDeptId = useAppStore((s) => s.selectedDepartmentId);
  const { data: departments } = useDepartments();
  const selectedDept = departments?.find((d) => d.id === selectedDeptId);
  const deptId = selectedDeptId ?? me.data?.department?.id;
  const deptName = isPlatformAdmin
    ? selectedDept?.name ??
      (selectedDeptId === "all" ? "All departments" : "Select a department")
    : me.data?.department?.name ?? "Loading department…";

  const summary = useSopComplianceSummary(deptId);
  const exceedances = useSopExceedances(deptId);
  const recommendations = useSopRecommendations(deptId);
  const roster = useSopRosterExposure(deptId);
  const ruleResults = useSopRuleResults(deptId);

  const isLoading =
    summary.isLoading ||
    exceedances.isLoading ||
    recommendations.isLoading ||
    roster.isLoading ||
    ruleResults.isLoading;
  const isError =
    summary.isError ||
    exceedances.isError ||
    recommendations.isError ||
    roster.isError ||
    ruleResults.isError;
  const ready =
    summary.data !== undefined &&
    exceedances.data !== undefined &&
    recommendations.data !== undefined &&
    roster.data !== undefined &&
    ruleResults.data !== undefined;

  // RES-43 instrumentation: dedicated view + dwell events for the demo/sandbox
  // evidence base. Tenant tagging rides the `department` super property that
  // `identifyUser` registers, so Gander demo traffic and future sandbox
  // traffic segment apart with no extra props here. All of it no-ops without
  // a Mixpanel token or outside a chief session.
  useEffect(() => {
    trackEvent(AnalyticsEvent.SopReportViewed);
  }, []);
  useDwellTracking(AnalyticsEvent.SopReportExited);

  return (
    <>
      <style>{`@media print { #sop-report > * { break-inside: avoid; } }`}</style>
      <div id="sop-report" className="mx-auto max-w-5xl space-y-8">
        <header className="space-y-2 border-b border-border pb-5">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              Exposure &amp; SOP Compliance
            </h1>
            <Badge variant="warning">Preview</Badge>
            <Button
              variant="outline"
              size="sm"
              className="ml-auto print:hidden"
              onClick={() => downloadPdf(deptName)}
            >
              Download PDF
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            {deptName} · scored from real exposure records against NIOSH limits.
            Evidence, not certification.
          </p>
        </header>

        {!deptId ? (
          isPlatformAdmin ? (
            <p className="text-sm text-muted-foreground">
              Select a department to view its SOP-compliance preview.
            </p>
          ) : (
            <Skeleton className="h-64" />
          )
        ) : isLoading ? (
          <div className="space-y-6">
            <Skeleton className="h-24" />
            <Skeleton className="h-48" />
            <Skeleton className="h-64" />
          </div>
        ) : isError ? (
          <p className="text-sm text-destructive">
            Failed to load exposure data for the SOP-compliance audit.
          </p>
        ) : ready ? (
          <>
            <TrackedSection name="kpi_strip">
              <KpiStrip data={summary.data!} />
            </TrackedSection>
            <TrackedSection name="co_exposure_gauge">
              <CoExposureGauge data={summary.data!} />
            </TrackedSection>
            <TrackedSection name="atmospheric_exceedances">
              <AtmosphericExceedances data={exceedances.data!} />
            </TrackedSection>
            <TrackedSection name="sop_recommendations">
              <SopRecommendations recs={recommendations.data!} />
            </TrackedSection>
            <TrackedSection name="department_sop_rules">
              <DepartmentSopRules data={ruleResults.data!} />
            </TrackedSection>
            <TrackedSection name="flagged_responses">
              <FlaggedResponses members={roster.data!} />
            </TrackedSection>
            <TrackedSection name="career_surveillance">
              <CareerSurveillance members={roster.data!} />
            </TrackedSection>
            <TrackedSection name="coverage_data_quality">
              <CoverageDataQuality data={summary.data!} />
            </TrackedSection>
            <TrackedSection name="method_references">
              <MethodReferences limitsVersion={summary.data!.limits.limits_version} />
            </TrackedSection>
          </>
        ) : null}
        <TrackedSection name="feedback">
          <SopFeedbackBlock />
        </TrackedSection>
      </div>
    </>
  );
}

/* ──────────────────────────── KPI strip ──────────────────────────────────── */

function KpiStrip({ data }: { data: SopComplianceSummary }) {
  const highestPeak = toNum(data.highest_co_peak_ppm);
  const ceiling = toNum(data.limits.co_ceiling_ppm) ?? 200;
  const peakOverCeiling = (highestPeak ?? 0) >= ceiling;
  const tiles: Array<{ label: string; value: string; bad?: boolean }> = [
    { label: "Records scored", value: fmtInt(data.records_total) },
    { label: "Firefighters monitored", value: fmtInt(data.monitored_count) },
    {
      label: "Over CO ceiling",
      value: fmtInt(data.records_co_ceiling_over),
      bad: data.records_co_ceiling_over > 0,
    },
    {
      label: "Highest CO peak (ppm)",
      value: fmtPpm(highestPeak),
      bad: peakOverCeiling,
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-xl border border-border bg-card p-4">
          <div
            className={`text-2xl font-semibold tabular-nums ${t.bad ? "text-destructive" : ""}`}
          >
            {t.value}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">{t.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ─────────────────── CO exposure vs NIOSH limits (hero) ──────────────────── */

function Marker({ pos, label }: { pos: number; label: string }) {
  return (
    <div
      className="absolute inset-y-0"
      style={{ left: `${pos}%` }}
      aria-hidden="true"
    >
      <div className="h-full w-px bg-foreground/40" />
      <div className="absolute top-full mt-1 -translate-x-1/2 whitespace-nowrap text-[10px] text-muted-foreground">
        {label}
      </div>
    </div>
  );
}

function CoExposureGauge({ data }: { data: SopComplianceSummary }) {
  const peak = toNum(data.highest_co_peak_ppm);
  const REL = toNum(data.limits.co_rel_ppm) ?? 35;
  const CEIL = toNum(data.limits.co_ceiling_ppm) ?? 200;
  const scaleMax = Math.max(peak ?? 0, CEIL) * 1.15;
  const pos = (v: number) => Math.min(100, (v / scaleMax) * 100);
  const overCeil = (peak ?? 0) >= CEIL;
  const overRel = (peak ?? 0) >= REL;
  const fill = overCeil ? "bg-destructive" : overRel ? "bg-warning" : "bg-success";
  const ceilCount = data.records_co_ceiling_over;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Carbon-monoxide exposure vs NIOSH limits</CardTitle>
        <p className="text-sm text-muted-foreground">
          Highest measured CO peak across all responses, against the {REL} ppm REL
          and the {CEIL} ppm ceiling.
        </p>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex items-baseline gap-2">
          <span
            className={`text-4xl font-semibold tabular-nums ${overCeil ? "text-destructive" : ""}`}
          >
            {fmtPpm(peak)}
          </span>
          <span className="text-sm text-muted-foreground">ppm highest peak</span>
        </div>
        <div className="relative mb-7 h-9 w-full overflow-visible rounded-md bg-muted">
          {peak !== null ? (
            <div
              className={`absolute inset-y-0 left-0 rounded-md ${fill} opacity-80`}
              style={{ width: `${pos(peak)}%` }}
            />
          ) : null}
          <Marker pos={pos(REL)} label={`REL ${REL}`} />
          <Marker pos={pos(CEIL)} label={`Ceiling ${CEIL}`} />
        </div>
        <p className="text-sm">
          {overCeil ? (
            <span className="text-destructive">
              {ceilCount} response{plural(ceilCount)} exceeded the {CEIL} ppm ceiling.
              Recommended action: adopt a measurable overhaul-clearance threshold
              (item&nbsp;A below).
            </span>
          ) : overRel ? (
            <span>
              Above the {REL} ppm REL, within the {CEIL} ppm ceiling.
            </span>
          ) : (
            <span className="text-success-foreground">
              Within every limit we measure. That means within scope, not
              certified safe.
            </span>
          )}
        </p>
      </CardContent>
    </Card>
  );
}

/* ───────────────────── Atmospheric exceedances table ─────────────────────── */

function AtmosphericExceedances({ data }: { data: SopExceedances }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Atmospheric exceedances</CardTitle>
        <p className="text-sm text-muted-foreground">
          Responses where a channel crossed its basis (of {data.records_total}{" "}
          scored). Total VOC is an internal screen, not a regulatory limit.
        </p>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-4 font-medium">Measure</th>
                <th className="py-2 pr-4 font-medium">Basis</th>
                <th className="py-2 pr-4 text-right font-medium">Exceedances</th>
                <th className="py-2 pr-4 text-right font-medium">Highest</th>
                <th className="py-2 text-right font-medium">SOG</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const highest = toNum(r.highest);
                return (
                  <tr key={r.measure_key} className="border-b border-border/60">
                    <td className="py-3 pr-4 font-medium">{r.measure_label}</td>
                    <td className="py-3 pr-4 text-muted-foreground">
                      {r.basis_label}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums">
                      <span
                        className={r.count > 0 ? "font-semibold text-destructive" : ""}
                      >
                        {r.count}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums text-muted-foreground">
                      {highest === null ? "—" : `${fmtPpm(highest)} ${r.unit}`}
                    </td>
                    <td className="py-3 text-right text-muted-foreground">
                      {r.sog_ref ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ───────────────── SOP improvement recommendations ───────────────────────── */

function RecommendationCard({ rec }: { rec: SopRecommendation }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold">
          {rec.title}
          <span className="ml-2 text-xs font-normal text-muted-foreground">
            {rec.sog_ref} · {rec.item}
          </span>
        </h3>
      </div>
      <p className="mt-2 text-sm italic text-muted-foreground">
        &ldquo;{rec.current_clause}&rdquo;
      </p>
      <p className="mt-1 text-sm">
        <span className="text-muted-foreground">Gap: </span>
        {rec.gap}
      </p>
      <p className="mt-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
        <span className="text-xs uppercase tracking-wide text-muted-foreground">
          {rec.proposed_label}
        </span>
        <br />
        {rec.proposed}
      </p>
      <p className="mt-2 text-xs text-muted-foreground">
        Evidence: {rec.evidence_basis}
      </p>
    </div>
  );
}

function SopRecommendations({ recs }: { recs: SopRecommendation[] }) {
  const anyIllustrative = recs.some((r) => r.illustrative);
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>SOP improvement recommendations</CardTitle>
          {anyIllustrative ? <Badge variant="outline">Illustrative</Badge> : null}
        </div>
        <p className="text-sm text-muted-foreground">
          Clauses and proposed wording are illustrative templates (your own
          approved SOP rules render below); the evidence figures are from real
          data.
        </p>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-2">
        {recs.map((rec) => (
          <RecommendationCard key={rec.rec_key} rec={rec} />
        ))}
      </CardContent>
    </Card>
  );
}

/* ─────────────────── Flagged responses (real, clickable) ─────────────────── */

function FlaggedResponses({ members }: { members: SopRosterMember[] }) {
  const flagged = members.filter((f) => !f.within_limits);
  if (flagged.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Flagged responses</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            No scored exceedances in the department&apos;s exposure records.
          </p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle>Flagged responses</CardTitle>
          <Badge variant="destructive">
            {flagged.length} firefighter{plural(flagged.length)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="space-y-3">
          {flagged.map((f) => (
            <li
              key={f.user_id}
              className="rounded-lg border border-destructive/30 bg-destructive/5 p-3"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{f.display_name}</span>
                <Badge variant="destructive">
                  {f.exceedances.length} incident{plural(f.exceedances.length)}
                </Badge>
              </div>
              <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                {f.exceedances.map((x, i) => (
                  <li key={`${x.incident_id}-${i}`}>
                    <Link
                      to={`/incidents/${x.incident_id}`}
                      className="text-primary hover:underline"
                      // No id prop: raw incident UUIDs never reach analytics.
                      onClick={() => trackEvent(AnalyticsEvent.SopIncidentOpened)}
                    >
                      {incidentDisplayName({
                        id: x.incident_id,
                        incident_type: x.incident_type,
                        address: x.address,
                        incident_number: x.incident_number,
                      })}
                    </Link>
                    {": "}
                    {x.reasons.join("; ")}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/* ─────────────────── Career exposure surveillance (bars) ─────────────────── */

function CareerSurveillance({ members }: { members: SopRosterMember[] }) {
  const withDose = members
    .filter((f) => f.has_data)
    .map((f) => ({ ...f, dose: toNum(f.co_cumulative_ppm_min) ?? 0 }))
    .sort((a, b) => b.dose - a.dose);
  const max = withDose.reduce((m, f) => Math.max(m, f.dose), 0);
  return (
    <Card className="bg-muted/20">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="text-muted-foreground">
            Career exposure surveillance
          </CardTitle>
          <Badge variant="outline">Surveillance only</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Cumulative CO dose per firefighter. This is a long-term trend to watch,
          not a pass or fail. There is no regulatory career limit for these doses.
        </p>
      </CardHeader>
      <CardContent>
        {withDose.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No firefighter has exposure history yet.
          </p>
        ) : (
          <ul className="space-y-3">
            {withDose.map((f) => {
              const peak = toNum(f.co_peak_ppm);
              return (
                <li
                  key={f.user_id}
                  className="grid grid-cols-[7rem_1fr_auto] items-center gap-3 text-sm sm:grid-cols-[10rem_1fr_auto]"
                >
                  <span className="truncate font-medium">{f.display_name}</span>
                  <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                    <div
                      className="h-full rounded-full bg-muted-foreground/60"
                      style={{
                        width: max > 0 ? `${Math.round((f.dose / max) * 100)}%` : "0%",
                      }}
                    />
                  </div>
                  <span className="whitespace-nowrap tabular-nums text-muted-foreground">
                    {fmtInt(f.dose)} ppm·min
                    {peak !== null ? ` · peak ${fmtPpm(peak)}` : ""}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ───────────────── Coverage & data quality ───────────────────────────────── */

function humanizeKey(key: string): string {
  return key.replace(/_/g, " ");
}

function CoverageDataQuality({ data }: { data: SopComplianceSummary }) {
  const limits = data.limits;
  const measured = [
    `Carbon monoxide: REL ${toNum(limits.co_rel_ppm)} / ceiling ${toNum(limits.co_ceiling_ppm)} ppm`,
    `Oxygen: ≥ ${toNum(limits.o2_deficiency_pct)}% atmospheric minimum`,
    `Combustible gas: ${toNum(limits.lel_action_pct)}% LEL action level`,
    `Total VOC: our own screen (> ${toNum(limits.voc_screen_ppm)} ppm), not a limit`,
  ];
  const declared = [
    "SCBA donned / doffed state",
    "Overhaul phase (IC-declared only)",
    "Benzene / PAH speciation",
    "Decontamination",
    "Rehabilitation vital signs",
    "Accountability tags",
  ];
  const confidences = Object.entries(data.data_quality.records_by_confidence);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Coverage &amp; data quality</CardTitle>
        <p className="text-sm text-muted-foreground">
          What we measure, and what your SOP calls for that we cannot measure
          yet. A green light means the readings stayed within what we measure. It
          never means safe.
        </p>
      </CardHeader>
      <CardContent className="grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="mb-2 text-sm font-semibold">Measured &amp; scored</h3>
          <ul className="space-y-1.5 text-sm">
            {measured.map((m) => (
              <li key={m} className="flex gap-2">
                <span className="text-success">●</span>
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="mb-2 text-sm font-semibold text-muted-foreground">
            Declared, not instrumented
          </h3>
          <ul className="space-y-1.5 text-sm text-muted-foreground">
            {declared.map((d) => (
              <li key={d} className="flex gap-2">
                <span className="text-muted-foreground/50">○</span>
                <span>{d}</span>
              </li>
            ))}
          </ul>
        </div>
        <p className="text-xs text-muted-foreground md:col-span-2">
          {data.records_total} record{plural(data.records_total)} scored
          {confidences.length > 0
            ? ` (${confidences
                .map(([k, v]) => `${v} ${humanizeKey(k)}`)
                .join(", ")})`
            : ""}
          {data.data_quality.records_flagged_peak_no_dose > 0
            ? `, ${data.data_quality.records_flagged_peak_no_dose} flagged for review`
            : ""}
          {data.data_quality.not_available.length > 0
            ? `; the measurements do not claim: ${data.data_quality.not_available
                .map(humanizeKey)
                .join(", ")}`
            : ""}
          ; readings are ambient at the worn monitor, not inhaled dose through
          SCBA.
        </p>
      </CardContent>
    </Card>
  );
}

/* ───────────────── Method, references & limitations ──────────────────────── */

function MethodReferences({ limitsVersion }: { limitsVersion: string }) {
  return (
    <div className="space-y-2 border-t border-border pt-5 text-xs text-muted-foreground">
      <p>
        <span className="font-medium text-foreground">Method.</span> Body-worn
        monitor readings time-aligned to each incident, compared against the bases
        above (limits {limitsVersion}); dose integrated over monitored worktime.
        Uncalibrated channels are flagged and excluded from scoring.
      </p>
      <p>
        <span className="font-medium text-foreground">References.</span> NIOSH Pocket
        Guide (CO) · ACGIH TLVs · NFPA 1500 · NFPA 1550.
      </p>
      <p>
        <span className="font-medium text-foreground">Limitations.</span> This is
        evidence, not certification. &ldquo;Within limits&rdquo; means within what
        our calibrated monitors measured. It is not a judgement that the scene was
        safe.
      </p>
    </div>
  );
}
